import _ from 'lodash';
import draggable from 'vuedraggable';
import { mapActions, mapState } from 'vuex';
import getConfiguration from '@/util/creativeFields';
import BoxWidget from '@/components/box-widget/BoxWidget.vue';
import Markdown from '~/components/markdown/Markdown.vue';
import { adapi, Debug, storeGetters, initDycolor, CreativePreview, Datetimepicker, creativeDefaults, moment, permissions, FileUpload } from '~';
import { GOAL_LEAD_GENERATION, CREATIVE_STATUSES, GOAL_LOCAL_OFFERS, COMMENTABLE_TYPES } from '@/util/constants';

initDycolor('adv-builder', require(`${process.env.DYCOLOR_REQUIRE}./AdvBuilder.scss`)); // eslint-disable-line

const debug = Debug('adv-builder');
const videoFormats = ['mp4', 'mov', 'qt', 'avi'];
const facebookVideoFormats = ['mp4', 'mov', 'qt', 'avi', 'gif'];

export default {
  components: {
    Markdown,
    Datetimepicker,
    draggable,
    BoxWidget,
    CreativePreview,
    FileUpload,
  },
  data() {
    return {
      creative: null, // Form input/media upload should manage only this object, to keep all in sync
      saving: false,
      savingType: false,
      moving: false,
      reject_reason: '',
    };
  },
  filters: {
    translateOptions(options, i18nPath = null, translator) {
      return options.map(value => ({
        value,
        text: translator(`${i18nPath}.${value}`),
      }));
    },
  },
  computed: {
    ...mapState('creative', ['current', 'callToActions']),
    ...mapState('genericConfig', ['config']),
    goalType() {
      return _.get(this.creative, 'campaign.campaign_rule.goal_type');
    },
    defaultCreativeValues() {
      return Object.assign(
        {},
        creativeDefaults.creative(this.settings),
        { media: creativeDefaults.media(this.settings) },
        { offer_config: creativeDefaults.offerConfig(this.settings) },
      );
    },
    configuration() {
      const shortFields = !!Number(_.get(this.config, 'creative_short_fields', false));
      const creativeType = _.get(this.creative, 'type', null);
      const goalType = this.goalType;

      const overwrite = {};
      const mediaCountMin = _.get(this.config, `goal_types.${goalType}.ad_formats.${creativeType}.min_cards`, undefined);
      const mediaCountMax = _.get(this.config, `goal_types.${goalType}.ad_formats.${creativeType}.max_cards`, undefined);

      if (typeof mediaCountMin !== 'undefined') {
        overwrite.media_count_min = mediaCountMin;
      } else if (typeof mediaCountMax !== 'undefined') {
        overwrite.media_count_max = mediaCountMax;
      }
      return getConfiguration(creativeType, shortFields, this.goalType, overwrite);
    },
    leadQuestionVisible() {
      if (this.configuration.default_question) {
        return !_.get(this.config, `goal_types.${GOAL_LEAD_GENERATION}.config.default_question_disabled`, true);
      }
      return false;
    },
    ctaOptions() {
      return Object.values(_.get(this.callToActions, _.get(this.creative, 'campaign.campaign_rule.goal_type'), {}))
        .map(item => ({
          value: item,
          text: this.$t('fn.complex.calltoaction', { calltoaction: item }),
        }));
    },
    canRemoveFiles() {
      return _.get(this.creative, 'media', []).length > this.configuration.media_count_min;
    },
    canAddFiles() {
      return _.get(this.creative, 'media', []).length < this.configuration.media_count_max;
    },
    canFinishCreative() {
      return [CREATIVE_STATUSES.BRIEF, CREATIVE_STATUSES.WIP].indexOf(this.creative.status) !== -1;
    },
    wipStatus() {
      return this.creative.status === CREATIVE_STATUSES.WIP;
    },
    isCarousel() {
      return _.get(this.creative, 'type', null) === 'carousel';
    },
    isSlideshow() {
      return _.get(this.creative, 'type', null) === 'slideshow';
    },
    dragOptions() {
      return {
        animation: 0,
        group: 'files',
        disabled: false,
        ghostClass: 'ghost',
        handle: '.handle',
      };
    },
    landingPage() {
      return _.get(this.creative, 'campaign.landingpages.[0]', null);
    },
    settings() {
      return {
        isOffer: this.goalType === GOAL_LOCAL_OFFERS,
        isGoalIsLeadGeneration: this.goalType === GOAL_LEAD_GENERATION,
        isOfferTypeOne: _.get(this.creative, 'offer_config.code_type') === 'one',
        isRedeemUrl: _.get(this.creative, 'offer_config.redeem') !== 'store',
        leadAdsEnabled: _.get(this.creative, 'campaign.lead_ads_enabled', false),
      };
    },
    permissions() {
      return permissions.getPermissions(this.creative);
    },
    createdAt() {
      return this.$t('fn.date', { timestamp: this.creative.created_at });
    },
  },
  watch: {
    current: {
      deep: true,
      immediate: true,
      handler: function watchCurrent(creative) {
        // Update data every time Creative store value updates
        const copy = Object.assign({}, creative);

        copy.media = copy.media
          .map(file => ({
            id: file.id,
            order: file.order,
            unique: this.getUniqueIdentificator(),
            name: file.name,
            description: file.description,
            link: file.link,
            isVideo: file.file ? this.isVideo(file.file) : undefined,
            file: {
              id: _.get(file, 'file.id', 0),
              name: _.get(file, 'file.file_name', null),
              size: _.get(file, 'file.file_size', null),
              path: _.get(file, 'file.path', null),
              thumbnail: _.get(file, 'file.thumbnail', null),
            },
          }));

        // Set default fields for lead ads if they don't exist
        if (!_.isObject(copy.lead_config)) {
          copy.lead_config = {};
        }

        if (!copy.lead_config.privacy_policy_link && _.get(copy, 'privacy_link', null)) {
          copy.lead_config.privacy_policy_link = _.get(copy, 'privacy_link', null);
        }

        if (!copy.lead_config.default_question && _.get(copy, 'form_question', null)) {
          copy.lead_config.default_question = _.get(copy, 'form_question', null);
        }

        ['privacy_policy_link', 'default_question'].forEach(value => {
          if (!_.has(copy, `lead_config.${value}`)) {
            copy.lead_config[value] = _.get(this.config, `goal_types.lead_generation.config.${value}`, null);
          }
        });

        copy.lead_photo = {
          id: _.get(copy, 'lead_photo.id', 0),
          name: _.get(copy, 'lead_photo.file_name', null),
          size: _.get(copy, 'lead_photo.file_size', null),
          path: _.get(copy, 'lead_photo.path', null),
          thumbnail: _.get(copy, 'lead_photo.thumbnail', null),
        };

        // Offer Settings
        if (!_.isObject(copy.offer_config)) {
          const campaignOfferDetails = _.get(copy, 'timeline', [])
            .filter(i => i.type === 'Comment')
            .map(i => i.data)
            .filter(i => i.type === 'brief')
            .map(i => _.get(i, 'details', []).filter(d => d.name === 'offer_config').find(() => true))
            .reverse()
            .find(() => true);
          const creativeOfferDetailsValues = _.get(copy, 'details', []).find(d => d.name === 'offer_config');

          copy.offer_config = Object.assign(
            {},
            this.defaultCreativeValues.offer_config,
            campaignOfferDetails ? campaignOfferDetails.value : {},
            creativeOfferDetailsValues ? creativeOfferDetailsValues.value : {},
          );
          let usedDate = moment();
          if (_.get(copy, 'campaign.start_date', null)) {
            usedDate = moment(_.get(copy.campaign, 'start_date', null));
            if (!usedDate.isValid()) {
              usedDate = moment();
              debug.info(`Invalid campaign start date:${_.get(copy.campaign, 'start_date', null)}`);
            }
          }
          // TODO: use predicted date for ASAP campaigns. (TICKET ID:28559)
          usedDate.add(1, 'day').set('hour', 0).set('minute', 0);
          copy.offer_config.date_range_start = usedDate.toISOString();
        }
        copy.cta_phone = _.get(copy.details.find(d => d.name === 'cta_phone'), 'value', '');
        this.creative = copy;
      },
    },
  },
  methods: {
    ...mapActions('creative', ['reloadCreative', 'completeCreative', 'getCallToActionList', 'moveForward', 'moveBack']),
    onMoveForward() {
      this.moving = true;
      const fin = () => this.moving = false;
      this.moveForward(this.creative.id)
        .then(() => {
          this.$toast.success({ message: this.$t('pos.campaign.creatives.adv-builder.complete') });
          fin();
        })
        .catch(error => {
          this.$toast.error({ message: adapi.translateAdapiErrors(error.errors, this.$t) });
          fin();
        });
    },
    reject() {
      this.moving = true;
      return this.moveBack({
        id: this.creative.id,
        data: {
          commentable_id: this.$route.params.id,
          commentable_type: COMMENTABLE_TYPES.CREATIVE,
          text: this.reject_reason,
          file_ids: null,
          comment_type: 'wip-reject',
        },
      })
        .then(() => {
          this.moving = false;
          if (this.$refs.comment) {
            this.$refs.comment.clearCommentData();
          }
          this.$toast.info({ message: this.$t('pos.creative.creative-rejected') });
          return this.reloadCreative();
        })
        .catch(e => {
          this.moving = false;
          this.$toast.error({ message: e.toString() });
        });
    },
    checkAndSave(complete) {
      const errors = [];
      const APP_STORES = ['play.google.com', 'itunes.apple.com'];

      if (this.creative.caption && APP_STORES.some(c => this.creative.caption.includes(c))) {
        errors.push(this.$t('pos.campaign.creatives.adv-builder.incorrect-url'));
      }

      if (
        !this.creative.caption
        && this.creative.media.some(m => m.link && APP_STORES.some(c => m.link.includes(c)))
      ) {
        errors.push(this.$t('pos.campaign.creatives.adv-builder.incorrect-url'));
      }

      if (complete && this.settings.isOffer && this.creative.media.some(m => !_.get(m, 'file.id'))) {
        errors.push(this.$t('pos.campaign.creatives.adv-builder.empty-media'));
      }

      if (errors.length) {
        errors.forEach(message => this.$toast.error({ message }));
      } else {
        this.save(complete);
      }
    },
    save(complete) {
      this.saving = true;
      this.savingType = complete ? 1 : 2;

      const fin = () => {
        this.saving = false;
        this.savingType = false;
      };
      // Send only required values
      let leadConfig = null;
      let leadPhotoId = null;
      if (this.settings.leadAdsEnabled) {
        leadConfig = {
          privacy_policy_link: this.creative.lead_config.privacy_policy_link,
          default_question: this.creative.lead_config.default_question,
        };
        leadPhotoId = _.get(this.creative, 'lead_photo.id', null);
      }
      const offerConfig = this.settings.isOffer ? _.get(this.creative, 'offer_config') : null;

      const data = {
        message: this.creative.message,
        name: this.creative.name,
        description: this.creative.description,
        caption: this.creative.caption,
        cta: this.creative.cta,
        transition_ms: this.creative.transition_ms,
        duration_ms: this.creative.duration_ms,
        lead_photo: leadPhotoId,
        lead_config: leadConfig,
        offer_config: offerConfig,
        media: this.$refs.draggable.realList.map((file, order) => ({
          imageId: _.get(file, 'id', 0),
          id: _.get(file, 'file.id', 0),
          description: _.get(file, 'description', null),
          name: _.get(file, 'name', null),
          link: _.get(file, 'link', null),
          order,
        })),
        cta_phone: this.creative.cta_phone,
      };

      if (complete) {
        data.complete = complete;
      }

      adapi.patch(`creatives/${storeGetters.getRouterParams().id}`, null, data)
        .then(result => {
          debug.info('success', result);
          this.$toast.success({ message: this.$t('pos.campaign.creatives.adv-builder.updated') });
          if (complete) {
            return this.completeCreative({
              id: storeGetters.getRouterParams().id,
              body: {
                forceToStatus: _.has(this.current, `statuses.${CREATIVE_STATUSES.HOLD}`) ? CREATIVE_STATUSES.HOLD : CREATIVE_STATUSES.READY,
                createPreview: true,
              },
            })
              .then(() => this.$toast.success({ message: this.$t('pos.campaign.creatives.adv-builder.complete') }));
          }

          return Promise.resolve();
        })
        .then(() => this.reloadCreative())
        .then(fin)
        .catch(error => {
          this.$toast.error({ message: adapi.translateAdapiErrors(error.errors, this.$t) });
          fin();
        });
    },
    addCard() {
      this.creative.media.push({
        id: 0,
        order: this.creative.media.length,
        unique: this.getUniqueIdentificator(),
        file: {
          name: null,
          description: null,
          link: null,
          isVideo: 0,
        },
      });
    },
    isVideo(file) {
      const path = _.get(file, 'path', null) ? file.path : '';
      return file && _.indexOf(videoFormats, path.split('.').pop()) !== -1 ? 1 : 0;
    },
    getFieldInfo(name, input = null) {
      const messages = [];
      if ([
        'message', 'name', 'description', 'caption',
        'offer_title', 'offer_details', 't_c', 'offer_code',
      ].includes(name)) {
        const elem = input ? _.get(this.creative, input) : this.creative[name];
        const size = _.size(elem);
        messages.push(this.$t('pos.campaign.creatives.adv-builder.info.max', {
          max: this.configuration[name],
          left: this.configuration[name] - size,
        }));
      } else if (['media_field_name', 'media_field_description'].includes(name)) {
        messages.push(this.$t('pos.campaign.creatives.adv-builder.info.max', {
          max: this.configuration[name],
          left: this.configuration[name] - _.size(input),
        }));
      } else if (['transition', 'duration'].includes(name)) {
        messages.push(this.$t('pos.campaign.creatives.adv-builder.info.min-int', { min: this.configuration[`${name}_ms`] }));
      } else if (['files'].includes(name)) {
        const videoExtensions = this.configuration.media_formats
          .filter(el => _.indexOf(facebookVideoFormats, el) !== -1);
        const imageExtensions = this.configuration.media_formats
          .filter(el => _.indexOf(facebookVideoFormats, el) === -1);
        if (imageExtensions.length > 0) {
          messages.push(this.$t('pos.campaign.creatives.adv-builder.info.files-image', {
            max: this.configuration.media_size / 1024,
            extensions: imageExtensions.join(', '),
          }));
        }
        if (videoExtensions.length > 0) {
          messages.push(this.$t('pos.campaign.creatives.adv-builder.info.files-video', {
            max: this.configuration.media_size / 1024,
            extensions: videoExtensions.join(', '),
          }));
        }
      }
      return messages.join('. ');
    },
    updateLeadPhoto(data) {
      this.creative.lead_photo = data[0];
    },
    updateMediaFile(unique, data) {
      const found = this.creative.media.find(f => f.unique === unique);
      if (found) {
        found.file = data[0];
        found.isVideo = this.isVideo(data[0]);
      }
    },
    removeMediaFile(unique) {
      this.creative.media = this.creative.media.filter(file => file.unique !== unique);
    },
    getUniqueIdentificator() {
      return Math.random().toString(36).substr(2, 9);
    },
    onMsChange(field, value) {
      // TODO: this is temporary fix, we should add normal validation to all fields
      if (Number(value) < this.configuration[field]) {
        this.creative[field] = this.configuration[field];
      }
    },
    fixCreativeInitialData() {
      // Add minimum count of empty cards
      const length = this.creative.media ? this.creative.media.length : 0;
      if (length < this.configuration.media_count_min) {
        for (let i = length; i < this.configuration.media_count_min; i += 1) {
          this.addCard();
        }
      }

      // Set default CTA
      if (!this.creative.cta) {
        this.creative.cta = _.get(
          this.callToActions,
          `${_.get(this.creative, 'campaign.campaign_rule.goal_type')}.0`,
        );
      }

      // Fix minimal transition values
      ['duration_ms', 'transition_ms'].forEach(field => {
        if (_.get(this.creative, field, 0) < this.configuration[field]) {
          this.creative[field] = this.configuration[field];
        }
      });
      this.creative.advInitialDataSet = true;
    },
  },
  beforeMount() {
    this.fixCreativeInitialData();
  },
  beforeUpdate() {
    if (this.creative.advInitialDataSet !== true) {
      this.fixCreativeInitialData();
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/adv-builder/AdvBuilder.js